import React, { useContext, useEffect, useState } from "react";
import {
  Card,
  Col,
  DatePicker,
  Row,
  Spin,
  Table,
  Typography,
  message,
  Statistic,
  Divider,
} from "antd";
import { AppContext } from "../context/AppContext";
import DoubleEntryService, { STANDARD_ACCOUNTS } from "../utils/doubleEntryService";
import moment from "moment";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const formatCurrency = (value) =>
  `ZMW ${Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const IncomeStatement = () => {
  const { company } = useContext(AppContext);
  const token = localStorage.getItem("token");

  const [loading, setLoading] = useState(true);
  const [incomeStatementData, setIncomeStatementData] = useState(null);
  const [dateRange, setDateRange] = useState([
    moment().startOf("year"),
    moment(),
  ]);

  useEffect(() => {
    fetchIncomeStatement();
  }, [company?.id, dateRange]);

  const fetchIncomeStatement = async () => {
    if (!company?.id || !token) return;

    setLoading(true);
    try {
      const [startDate, endDate] = dateRange;

      const data = await DoubleEntryService.getIncomeStatement({
        company: company.id,
        token,
        startDate: startDate.format("YYYY-MM-DD"),
        endDate: endDate.format("YYYY-MM-DD"),
      });

      setIncomeStatementData(data);
    } catch (error) {
      console.error("Error fetching income statement:", error);
      message.error("Failed to load income statement");
    } finally {
      setLoading(false);
    }
  };

  const accountColumns = [
    {
      title: "Account",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Amount",
      dataIndex: "balance",
      key: "balance",
      align: "right",
      render: (value) => formatCurrency(Math.abs(value)),
    },
  ];

  const revenueData = incomeStatementData
    ? Object.values(incomeStatementData.revenue)
    : [];
  const expenseData = incomeStatementData
    ? Object.values(incomeStatementData.expenses)
    : [];

  const netIncome = incomeStatementData
    ? incomeStatementData.totalRevenue - incomeStatementData.totalExpenses
    : 0;

  return (
    <div className="page-container">
      <Row gutter={[16, 16]} align="middle" style={{ marginBottom: 24 }}>
        <Col xs={24} md={14}>
          <Title level={3} style={{ marginBottom: 0 }}>
            Income Statement (Profit & Loss)
          </Title>
          <Text type="secondary">
            Revenue and expenses for the period {dateRange[0].format("MMM DD, YYYY")} to{" "}
            {dateRange[1].format("MMM DD, YYYY")}
          </Text>
        </Col>
        <Col xs={24} md={10}>
          <RangePicker
            value={dateRange}
            onChange={(range) => range && setDateRange(range)}
            style={{ width: "100%" }}
            format="YYYY-MM-DD"
          />
        </Col>
      </Row>

      {/* Summary Cards */}
      {!loading && incomeStatementData && (
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title="Total Revenue"
                value={incomeStatementData.totalRevenue}
                precision={2}
                prefix="ZMW"
                valueStyle={{ color: "#3f8600" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title="Total Expenses"
                value={incomeStatementData.totalExpenses}
                precision={2}
                prefix="ZMW"
                valueStyle={{ color: "#cf1322" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title="Net Income"
                value={netIncome}
                precision={2}
                prefix="ZMW"
                valueStyle={{
                  color: netIncome >= 0 ? "#52c41a" : "#ff4d4f",
                  fontWeight: "bold",
                }}
              />
            </Card>
          </Col>
        </Row>
      )}

      <Spin spinning={loading}>
        {incomeStatementData && (
          <Card>
            {/* Revenue Section */}
            <div style={{ marginBottom: 24 }}>
              <Title level={4} style={{ color: "#3f8600" }}>
                REVENUE
              </Title>
              <Table
                dataSource={revenueData}
                columns={accountColumns}
                rowKey="code"
                pagination={false}
                size="small"
                summary={() => (
                  <Table.Summary.Row style={{ backgroundColor: "#f0f9ff" }}>
                    <Table.Summary.Cell>
                      <Text strong>Total Revenue</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell align="right">
                      <Text strong style={{ fontSize: 16, color: "#3f8600" }}>
                        {formatCurrency(incomeStatementData.totalRevenue)}
                      </Text>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                )}
              />
            </div>

            <Divider />

            {/* Expenses Section */}
            <div style={{ marginBottom: 24 }}>
              <Title level={4} style={{ color: "#cf1322" }}>
                EXPENSES
              </Title>
              <Table
                dataSource={expenseData}
                columns={accountColumns}
                rowKey="code"
                pagination={false}
                size="small"
                summary={() => (
                  <Table.Summary.Row style={{ backgroundColor: "#fff1f0" }}>
                    <Table.Summary.Cell>
                      <Text strong>Total Expenses</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell align="right">
                      <Text strong style={{ fontSize: 16, color: "#cf1322" }}>
                        {formatCurrency(incomeStatementData.totalExpenses)}
                      </Text>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                )}
              />
            </div>

            <Divider style={{ borderColor: "#000", borderWidth: 2 }} />

            {/* Net Income */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "16px 8px",
                backgroundColor: netIncome >= 0 ? "#f6ffed" : "#fff1f0",
                borderRadius: 4,
              }}
            >
              <Text strong style={{ fontSize: 18 }}>
                NET INCOME {netIncome < 0 ? "(LOSS)" : ""}
              </Text>
              <Text
                strong
                style={{
                  fontSize: 18,
                  color: netIncome >= 0 ? "#52c41a" : "#ff4d4f",
                }}
              >
                {formatCurrency(Math.abs(netIncome))}
              </Text>
            </div>
          </Card>
        )}

        {!loading && !incomeStatementData && (
          <Card>
            <div style={{ textAlign: "center", padding: "40px" }}>
              <Text type="secondary">
                No accounting data available for the selected period.
              </Text>
            </div>
          </Card>
        )}
      </Spin>

      {/* Explanation */}
      <Card style={{ marginTop: 16 }}>
        <Title level={5}>Understanding the Income Statement</Title>
        <Text>
          The income statement (also called Profit & Loss statement) shows your
          company's financial performance over a period of time:
        </Text>
        <ul>
          <li>
            <Text strong>Revenue</Text> - Income earned from interest, fees, and
            other sources
          </li>
          <li>
            <Text strong>Expenses</Text> - Costs incurred to operate the business
          </li>
          <li>
            <Text strong>Net Income</Text> - The profit (or loss) after deducting
            all expenses from revenue
          </li>
        </ul>
        <Text>
          Formula: <Text strong>Net Income = Total Revenue - Total Expenses</Text>
        </Text>
        <br />
        <Text type="secondary" style={{ fontSize: 12, marginTop: 8, display: "block" }}>
          This report uses double-entry accounting data from journal entries, ensuring
          accuracy and compliance with professional accounting standards.
        </Text>
      </Card>
    </div>
  );
};

export default IncomeStatement;
